import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { eventAPI } from '../services/api';
import { useAuth } from './AuthContext';

const EventsContext = createContext();

export const useEvents = () => {
    const context = useContext(EventsContext);
    if (!context) throw new Error('useEvents must be used within an EventsProvider');
    return context;
};

export const EventsProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [events, setEvents] = useState([]);
    const [archivedEvents, setArchivedEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchEvents = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await eventAPI.getAll();
            setEvents(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch events');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchArchived = useCallback(async () => {
        try {
            const response = await eventAPI.getArchived();
            setArchivedEvents(response.data);
        } catch (err) {
            console.error('Failed to fetch archived events:', err);
        }
    }, []);

    const createEvent = useCallback(async (eventData) => {
        const response = await eventAPI.create(eventData);
        setEvents(prev => [response.data, ...prev]);
        return response.data;
    }, []);

    const updateEvent = useCallback(async (eventId, eventData) => {
        const response = await eventAPI.update(eventId, eventData);
        setEvents(prev => prev.map(e => e._id === eventId ? response.data : e));
        return response.data;
    }, []);

    const toggleArchive = useCallback(async (eventId) => {
        await eventAPI.toggleArchive(eventId);
        // Remove from active list, refresh archived
        setEvents(prev => prev.filter(e => e._id !== eventId));
        fetchArchived();
    }, [fetchArchived]);

    const unarchiveEvent = useCallback(async (eventId) => {
        await eventAPI.toggleArchive(eventId);
        setArchivedEvents(prev => prev.filter(e => e._id !== eventId));
        fetchEvents();
    }, [fetchEvents]);

    const deleteEvent = useCallback(async (eventId) => {
        await eventAPI.delete(eventId);
        setEvents(prev => prev.filter(e => e._id !== eventId));
        setArchivedEvents(prev => prev.filter(e => e._id !== eventId));
    }, []);

    const getEventWithMaps = useCallback(async (eventId) => {
        const response = await eventAPI.getById(eventId);
        return response.data;
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            fetchEvents();
        } else {
            setEvents([]);
            setArchivedEvents([]);
        }
    }, [isAuthenticated, fetchEvents]);

    return (
        <EventsContext.Provider value={{
            events,
            archivedEvents,
            loading,
            error,
            fetchEvents,
            fetchArchived,
            createEvent,
            updateEvent,
            toggleArchive,
            unarchiveEvent,
            deleteEvent,
            getEventWithMaps
        }}>
            {children}
        </EventsContext.Provider>
    );
};